import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { JSX, useCallback, useEffect, useState } from "react";
import { CombinedProblemConfigs, defaultConfigs } from "src/lib/ProblemGenerators/CombinedProblemGenerator";
import Button from "./Button";
import { Presets } from "src/lib/ProblemGenerators/QuestionSetProblemGenerator";
import { DefaultProgressionConfigs, ProgressionConfigs } from "../lib/ProblemGenerators/ProgressionDefaults";

type ConfigMethod = {
    id: string;
    name: string;
    description: string;
}
const configMethods: Array<ConfigMethod> = [
    { 
        id: "setSelection",
        name: "Question Sets",
        description: "Specify questions asked based on predetermined question sets"
    },
    {
        id: "custom",
        name: "Custom",
        description: "Set the frequency and operand ranges manually for each operation"
    }, 
    {
        id: "progressive",
        name: "Progressive",
        description: "Progressively add more difficult questions for 'win' streaks."
    }
]
type TopLevelSettings = {
    endCondition: "totalQuestions" | "timeLimit";
    totalQuestions: number;
    timeLimit: number;
    problemSelection: "setSelection" | "custom" | "progressive";
}
export const defaultTopLevelSettings = {
    endCondition: "totalQuestions",
    totalQuestions: 40,
    timeLimit: 600,
    problemSelection: "setSelection"
} as TopLevelSettings;

export const defaultCompleteConfigs : CompleteSettings = {
    topLevel: defaultTopLevelSettings,
    generatorSettings: defaultConfigs,
    setSelection: ["Single Digit Addition"],
    progression: DefaultProgressionConfigs
}
export type CompleteSettings = {
    setSelection: Array<string>;
    generatorSettings: CombinedProblemConfigs;
    topLevel: TopLevelSettings;
    progression: ProgressionConfigs;
}
type Props = {
    show: boolean;
    saveClicked?: (newConfigs: CompleteSettings) => void;
    cancelClicked?: () => void;
    currentConfigs: CompleteSettings;
}
export default function Settings( props: Props ) {
    //const [ selectedConfigMethod, setSelectedConfigMethod] = useState("setSelection");
    const [internalConfigs, setInternalConfigs ] = useState<CompleteSettings>( defaultCompleteConfigs );

    const setConfigProperty = useCallback((propertyPath: string, value: number | string | boolean | undefined) => {
        setInternalConfigs((currentValue) => { 
            const pathChunks = propertyPath.split('.');
            const cloned = structuredClone(currentValue);
            let c: any = cloned;

            for (let i = 0;i<pathChunks.length;i++) {
                if (i < pathChunks.length - 1) {
                    if (c[pathChunks[i]] === null || c[pathChunks[i]] === undefined)
                        Object.assign(c, { [pathChunks[i]]: {} });
                    c = c[pathChunks[i]]
                }
                else {
                    Object.assign(c, { [pathChunks[i]]: value })
                }
            }
            return cloned;
            
        })
    }, [ setInternalConfigs ])

    const resetToDefault = useCallback(() => {
        setInternalConfigs(props.currentConfigs);
    }, [ props.currentConfigs, setInternalConfigs ])

    /*
    const configTypeChanged = useCallback((configType: string) => {
        setSelectedConfigMethod(configType);
    }, [setSelectedConfigMethod])
    */

    const setSelectionChanged = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setInternalConfigs((current) => {
            let newConfigs = structuredClone(current);
            let newSelection = newConfigs.setSelection;

            if (event.target.checked && !current.setSelection.includes(event.target.value)) {
                newSelection.push(event.target.value);
            }
            else if (!event.target.checked && current.setSelection.includes(event.target.value)) {
                let matchIdx = newSelection.findIndex((v) => v === event.target.value);
                newSelection.splice(matchIdx, 1);
            }

            return newConfigs;
        })

    }, [ setInternalConfigs ])


    useEffect(() => {
        setInternalConfigs(props.currentConfigs);
    }, [ props.currentConfigs, setInternalConfigs ])

    return (
        <div className={`${props.show ? "" : "hidden"} absolute m-auto bg-white h-full w-full p-4`}>
            <div className="text-xl font-bold mb-2 flex flex-row items-center">
                <FontAwesomeIcon className="pr-4" icon={faArrowLeft} onClick={() => { if (props.saveClicked) props.saveClicked(internalConfigs)}}/>
                Settings
            </div>
            <div>
                <ConfigSection title="End Condition" titleSize="large">
                    <div>Condition</div>
                    <div>
                        <select value={internalConfigs.topLevel.endCondition } onChange={(event) => { setConfigProperty("topLevel.endCondition", event.target.value)}}>
                            <option value="totalQuestions">Number of Questions</option>
                            <option value="timeLimit">Time Limit</option>
                        </select>
                    </div>
                    <ConfigInput 
                        title="# of Questions" 
                        hidden={internalConfigs.topLevel.endCondition !== "totalQuestions"} 
                        value={internalConfigs.topLevel.totalQuestions} 
                        onChange={(value) => { setConfigProperty("topLevel.totalQuestions", value)}}
                    />
                    <ConfigInput 
                        hidden={internalConfigs.topLevel.endCondition !== "timeLimit"} 
                        title="Time Limit" units="seconds" 
                        value={internalConfigs.topLevel.timeLimit} 
                        onChange={(value) => { setConfigProperty("topLevel.timeLimit", value)}}
                    />
                </ConfigSection>
                <ConfigSection title="Problem Selection" titleSize="large">
                    <div>Selection Method</div>
                    <div>
                        <select value={internalConfigs.topLevel.problemSelection} onChange={(event) => { setConfigProperty("topLevel.problemSelection", event.target.value)}}>
                            {
                                configMethods.map((method, idx) => {
                                    return (<option key={idx} value={method.id}>{method.name}</option>)
                                })
                            }
                        </select>
                    </div>
                </ConfigSection>
                <div>
                    <div className={`${internalConfigs.topLevel.problemSelection === "setSelection" ? "block" : "hidden"} flex flex-col space-y-2 pt-4`}>
                        <div className="font-bold">Available Problem Sets</div>
                        {
                            ["Addition", "Subtraction", "Multiplication", "Division"].flatMap((category, idx) => {
                                return (
                                    <React.Fragment key={idx}>
                                        <div key={idx} className="text-slate-700">{category}</div>
                                        {
                                            Object.keys(Presets).map((presetKey, idx) => {
                                                if (Presets[presetKey].category === category) {
                                                    return (
                                                        <div key={idx} className="flex flex-row space-x-2">
                                                            <input type="checkbox" checked={internalConfigs.setSelection.includes(presetKey)} value={presetKey} onChange={(event) => { setSelectionChanged(event)  }}/>
                                                            <div>
                                                                <div className="">{presetKey}</div>
                                                                <div className="tracking-wide text-sm text-slate-500">{Presets[presetKey].description}</div>
                                                            </div>
                                                        </div>
                                                    )
                                                }
                                            })

                                        }
                                    </React.Fragment>
                                )
                            })
                        }
                    </div>
                    <div className={`${internalConfigs.topLevel.problemSelection === "custom" ? "block" : "hidden"}`}>
                        <ConfigSection title="Addition">
                            <ConfigInput title="Frequency" value={internalConfigs.generatorSettings?.addition?.frequency} onChange={(value) => { setConfigProperty("generatorSettings.addition.frequency", value)}}/>
                            <ConfigInput title="Max Sum" value={internalConfigs.generatorSettings?.addition?.maxResult} onChange={(value) => { setConfigProperty("generatorSettings.addition.maxResult", value)}}/>
                            <ConfigInput title="Operand 1 Min" value={internalConfigs.generatorSettings?.addition?.operand1?.min} onChange={(value) => { setConfigProperty("generatorSettings.addition.operand1.min", value)}}/>
                            <ConfigInput title="Operand 1 Max" value={internalConfigs.generatorSettings?.addition?.operand1?.max} onChange={(value) => { setConfigProperty("generatorSettings.addition.operand1.max", value)}}/>
                            <ConfigInput title="Operand 2 Min" value={internalConfigs.generatorSettings?.addition?.operand2?.min} onChange={(value) => { setConfigProperty("generatorSettings.addition.operand2.min", value)}}/>
                            <ConfigInput title="Operand 2 Max" value={internalConfigs.generatorSettings?.addition?.operand2?.max} onChange={(value) => { setConfigProperty("generatorSettings.addition.operand2.max", value)}}/>
                        </ConfigSection>
                        <ConfigSection title="Subtraction">
                            <ConfigInput title="Frequency" value={internalConfigs.generatorSettings?.subtraction?.frequency} onChange={(value) => { setConfigProperty("generatorSettings.subtraction.frequency", value)}}/>
                            <ConfigInput title="Operand 1 Min" value={internalConfigs.generatorSettings?.subtraction?.operand1?.min} onChange={(value) => { setConfigProperty("generatorSettings.subtraction.operand1.min", value)}}/>
                            <ConfigInput title="Operand 1 Max" value={internalConfigs.generatorSettings?.subtraction?.operand1?.max} onChange={(value) => { setConfigProperty("generatorSettings.subtraction.operand1.max", value)}}/>
                            <ConfigInput title="Operand 2 Min" value={internalConfigs.generatorSettings?.subtraction?.operand2?.min} onChange={(value) => { setConfigProperty("generatorSettings.subtraction.operand2.min", value)}}/>
                            <ConfigInput title="Operand 2 Max" value={internalConfigs.generatorSettings?.subtraction?.operand2?.max} onChange={(value) => { setConfigProperty("generatorSettings.subtraction.operand2.max", value)}}/>
                        </ConfigSection>
                        <ConfigSection title="Multiplication">
                            <ConfigInput title="Frequency" value={internalConfigs.generatorSettings?.multiplication?.frequency} onChange={(value) => { setConfigProperty("generatorSettings.multiplication.frequency", value)}}/>
                            <ConfigInput title="Operand 1 Min" value={internalConfigs.generatorSettings?.multiplication?.operand1?.min} onChange={(value) => { setConfigProperty("generatorSettings.multiplication.operand1.min", value)}}/>
                            <ConfigInput title="Operand 1 Max" value={internalConfigs.generatorSettings?.multiplication?.operand1?.max} onChange={(value) => { setConfigProperty("generatorSettings.multiplication.operand1.max", value)}}/>
                            <ConfigInput title="Operand 2 Min" value={internalConfigs.generatorSettings?.multiplication?.operand2?.min} onChange={(value) => { setConfigProperty("generatorSettings.multiplication.operand2.min", value)}}/>
                            <ConfigInput title="Operand 2 Max" value={internalConfigs.generatorSettings?.multiplication?.operand2?.max} onChange={(value) => { setConfigProperty("generatorSettings.multiplication.operand2.max", value)}}/>
                        </ConfigSection>
                        <ConfigSection title="Division">
                            <ConfigInput title="Frequency" value={internalConfigs.generatorSettings?.division?.frequency} onChange={(value) => { setConfigProperty("generatorSettings.division.frequency", value)}}/>
                            <ConfigInput title="Factor 1 Min" value={internalConfigs.generatorSettings?.division?.operand1?.min} onChange={(value) => { setConfigProperty("generatorSettings.division.operand1.min", value)}}/>
                            <ConfigInput title="Factor 1 Max" value={internalConfigs.generatorSettings?.division?.operand1?.max} onChange={(value) => { setConfigProperty("generatorSettings.division.operand1.max", value)}}/>
                            <ConfigInput title="Factor 2 Min" value={internalConfigs.generatorSettings?.division?.operand2?.min} onChange={(value) => { setConfigProperty("generatorSettings.division.operand2.min", value)}}/>
                            <ConfigInput title="Factor 2 Max" value={internalConfigs.generatorSettings?.division?.operand2?.max} onChange={(value) => { setConfigProperty("generatorSettings.division.operand2.max", value)}}/>
                        </ConfigSection>
                        <div className="mt-2">
                            <Button variant="destructive" onClick={() => { resetToDefault() }}>Undo Changes</Button>
                        </div>
                    </div>
                    <div className={`${internalConfigs.topLevel.problemSelection === "progressive" ? "block" : "hidden"}`}>
                        <div className="p-2 text-slate-500">
                            This mode starts with easier questions for each of the operations included.  As questions are answered correctly, they will get more and more difficult.
                        </div>
                        <ConfigSection title="">
                            <ConfigInput title="Progression Rate" description="Lower progresses faster" inputType="number" value={internalConfigs.progression?.progressionRate} onChange={(value) => { setConfigProperty("progression.progressionRate", value)}}/>
                        </ConfigSection>
                        <ConfigSection title="Addition">
                            <ConfigInput title="Include" inputType="boolean" value={internalConfigs.progression?.addition?.include} onChange={(value) => { setConfigProperty("progression.addition.include", value)}}/>
                            <ConfigInput title="Max Sum" value={internalConfigs.progression?.addition?.maxResult} onChange={(value) => { setConfigProperty("progression.addition.maxResult", value) }}/>
                        </ConfigSection>
                        <ConfigSection title="Subtraction">
                            <ConfigInput title="Include" inputType="boolean" value={internalConfigs.progression?.subtraction?.include} onChange={(value) => { setConfigProperty("progression.subtraction.include", value)}}/>
                            <ConfigInput title="Operand 1 Max" value={internalConfigs.progression?.subtraction?.operand1?.max} onChange={(value) => { setConfigProperty("progression.subtraction.operand1.max", value) }}/>
                        </ConfigSection>
                        <ConfigSection title="Multiplication">
                            <ConfigInput title="Include" inputType="boolean" value={internalConfigs.progression?.multiplication?.include} onChange={(value) => { setConfigProperty("progression.multiplication.include", value)}}/>
                            <ConfigInput title="Operand 1 Max" value={internalConfigs.progression?.multiplication?.operand1?.max} onChange={(value) => { setConfigProperty("progression.multiplication.operand1.max", value)}}/>
                        </ConfigSection>
                        <ConfigSection title="Division">
                            <ConfigInput title="Include" inputType="boolean" value={internalConfigs.progression?.division?.include} onChange={(value) => { setConfigProperty("progression.division.include", value)}}/>
                            <ConfigInput title="Max Factor" value={internalConfigs.progression?.division?.operand1?.max} onChange={(value) => { setConfigProperty("progression.division.operand1.max", value)}}/>
                        </ConfigSection>
                        <div className="mt-2">
                            <Button variant="destructive" onClick={() => { resetToDefault() }}>Undo Changes</Button>
                        </div>
                    </div>
                </div>

            </div>
        </div>     
    )
}

function ConfigSection(props: { title: string, titleSize?: "regular" | "large", children: Array<JSX.Element> | JSX.Element }) {
    return (
        <>
            <div className={`font-bold mt-6 ${ ["regular", undefined].includes(props.titleSize) ? "" : "text-lg"}`}>{props.title}</div>
            <div className="pl-2 space-y-1 grid grid-cols-[max-content_auto] w-fit">
                { props.children }
            </div>
        </>
        
    )
}
function ConfigInput(props: { hidden?: boolean, title: string, units?: string, value: number | boolean | undefined, onChange?: (value: number | boolean | undefined) => void, inputType?: "number" | "boolean", description?: string }) {
    const [ redraw, setRedraw ] = useState(0);
    useEffect(() => {
        setRedraw((value) => value + 1);
    }, [ props.value ])


    if (props.inputType === "number" || props.inputType === undefined) {
        return (
            <>
                <div className={props.hidden ? "hidden" : "block"}>{props.title}</div>
                <div className={props.hidden ? "hidden" : "inline-block"}>
                    <input type="number" inputMode="numeric" value={props.value as (number | undefined) ?? "" } onChange={(event) => { if (props.onChange) props.onChange(event.target.value === "" ? undefined : parseInt(event.target.value))}}/><span className="pl-2 text-slate-400">{props.units}</span>
                    { props.description !== undefined ? <div className="pl-4 text-slate-400">{props.description}</div> : undefined }
                </div>
            </>
        )
    } else if (props.inputType === "boolean" || typeof props.value === "boolean") {
        return (
            <>
                <div className={props.hidden ? "hidden" : "block"}>{props.title}</div>
                <div className={props.hidden ? "hidden" : "block"}>
                    <input type="checkbox" checked={(props.value as boolean) ?? false} onChange={(event) => { if (props.onChange) props.onChange(event.target.checked)}}/>
                </div>
            </>
        )
    }
}